import { useRef, useEffect, useLayoutEffect, type ChangeEvent, type KeyboardEvent } from 'react'
import { Plus, ArrowUp, Square, X, FileText } from 'lucide-react'

interface Props {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onStop?: () => void
  onAttach?: () => void
  /** Base64 data URL of the attached image */
  attachedImage?: string | null
  /** Non-image file metadata for preview */
  attachedFile?: { name: string; size: string } | null
  onRemoveImage?: () => void
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
}

export default function ChatInput({
  value,
  onChange,
  onSend,
  onStop,
  onAttach,
  attachedImage,
  attachedFile,
  onRemoveImage,
  disabled = false,
  placeholder = 'Message...',
  autoFocus = false,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`
    }
  }

  useLayoutEffect(() => {
    if (!autoFocus) return
    textareaRef.current?.focus()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    if (value === '') el.style.height = 'auto'
  }, [value])

  // Mobile UX: Enter = new line, send only via button
  const handleKeyDown = (_e: KeyboardEvent) => { /* intentionally empty */ }

  const canSend = value.trim().length > 0 || !!attachedImage || !!attachedFile

  return (
    <div
      className="shrink-0 px-3 pt-2"
      style={{
        backgroundColor: 'var(--tg-theme-bg-color)',
        paddingBottom: 'calc(10px + max(var(--safe-bottom, 0px), env(safe-area-inset-bottom, 0px)))',
      }}
    >
      <div className="flex items-end gap-2">
        {/* Attach "+" button — always visible */}
        <button
          onClick={onAttach}
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 active:opacity-70 transition-opacity mb-0.5"
          style={{
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            color: 'var(--tg-theme-hint-color)',
          }}
        >
          <Plus size={18} />
        </button>

        {/* Input pill */}
        <div
          className="flex-1 flex flex-col"
          style={{
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            borderRadius: '22px',
          }}
        >
          {/* Image thumbnail preview */}
          {attachedImage && (
            <div className="relative self-start m-2 mb-0">
              <img
                src={attachedImage}
                alt="attachment"
                className="rounded-[10px] object-cover"
                style={{ width: '72px', height: '72px' }}
              />
              <button
                onClick={onRemoveImage}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--tg-theme-hint-color)', color: '#fff' }}
              >
                <X size={11} />
              </button>
            </div>
          )}

          {/* Non-image file preview */}
          {attachedFile && (
            <div className="relative self-start m-2 mb-0 flex items-center gap-2 px-3 py-2 rounded-[10px]"
              style={{ backgroundColor: 'rgba(0,0,0,0.06)', maxWidth: '200px' }}
            >
              <FileText size={18} style={{ color: 'var(--tg-theme-accent-text-color)', flexShrink: 0 }} />
              <div className="min-w-0">
                <p className="text-[12px] font-medium truncate" style={{ color: 'var(--tg-theme-text-color)' }}>{attachedFile.name}</p>
                <p className="text-[11px]" style={{ color: 'var(--tg-theme-hint-color)' }}>{attachedFile.size}</p>
              </div>
              <button
                onClick={onRemoveImage}
                className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'var(--tg-theme-hint-color)', color: '#fff' }}
              >
                <X size={9} />
              </button>
            </div>
          )}

          {/* Textarea */}
          <div className="flex items-end px-4 py-2.5">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus={autoFocus}
              rows={1}
              className="flex-1 resize-none text-[16px] outline-none bg-transparent leading-snug"
              style={{
                color: 'var(--tg-theme-text-color)',
                caretColor: 'var(--tg-theme-accent-text-color)',
                minHeight: '22px',
                maxHeight: '120px',
              }}
            />
          </div>
        </div>

        {/* Send / Stop */}
        <div className="shrink-0 mb-0.5">
          {disabled && onStop ? (
            <button
              onClick={onStop}
              className="w-9 h-9 rounded-full flex items-center justify-center active:opacity-70 transition-opacity"
              style={{
                backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                color: 'var(--tg-theme-hint-color)',
              }}
            >
              <Square size={12} fill="currentColor" />
            </button>
          ) : (
            <button
              onClick={onSend}
              disabled={!canSend}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity disabled:opacity-25 active:opacity-70"
              style={{
                backgroundColor: canSend
                  ? 'var(--tg-theme-button-color)'
                  : 'var(--tg-theme-secondary-bg-color)',
                color: canSend
                  ? 'var(--tg-theme-button-text-color)'
                  : 'var(--tg-theme-hint-color)',
              }}
            >
              <ArrowUp size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
